import useSWR from 'swr';
import { Box, Circle, Flex, Icon, Text } from '@chakra-ui/react';
import { fetchData } from '../../services/fetch';

// EG-24: acompanhamento procedural (stepper HORIZONTAL) das etapas + validade +
// callout do status atual com o motivo. Recebe o control já resolvido.

const STAGES = [
  { label: 'Em auditoria', statuses: ['em auditoria', 'em avaliação'] },
  { label: 'Em ajustes', statuses: ['em ajustes', 'aguardando adequações'] },
  { label: 'Conferência final', statuses: ['em conferência final', 'em revisão'] },
  { label: 'Auditado', statuses: ['auditado', 'avaliado'] },
];

const STATUS_LABELS = {
  'não enviado': 'Não enviado',
  'em avaliação': 'Em avaliação',
  'aguardando adequações': 'Aguardando adequações',
  'em revisão': 'Em revisão',
  avaliado: 'Avaliado',
  'em auditoria': 'Em auditoria',
  'em ajustes': 'Em ajustes',
  'em conferência final': 'Em conferência final',
  auditado: 'Auditado',
};

const ROLE_LABELS = {
  RESPONSAVEL: 'Consultor',
  MASTER: 'Consultor',
  RESPONDENTE: 'Cliente',
  AVALIADOR: 'Auditor',
};

const STATUS_COLOR = {
  'não enviado': '#5C6066',
  'em avaliação': '#EA582B',
  'aguardando adequações': '#FF9052',
  'em revisão': '#EAA72B',
  avaliado: '#21B84C',
  'em auditoria': '#EA582B',
  'em ajustes': '#FF9052',
  'em conferência final': '#EAA72B',
  auditado: '#21B84C',
};

function stageIndexOf(status) {
  const idx = STAGES.findIndex((s) => s.statuses.includes(status));
  return idx === -1 ? 0 : idx;
}

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString('pt-BR') : '';
}

// valid_until é um campo date (YYYY-MM-DD): formata sem Date() p/ não retroceder
// 1 dia por timezone.
function formatDateOnly(value) {
  if (!value) return '';
  const [y, m, d] = String(value).slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : formatDate(value);
}

export default function CertificationStatusPanel({ control }) {
  const { data: history } = useSWR(
    control?.id ? `certificate-status-control/${control.id}/history` : null,
    fetchData
  );

  if (!control?.status) return null;

  const currentIndex = stageIndexOf(control.status);
  const lastEventForStage = (statuses) => {
    if (!history?.length) return undefined;
    return [...history].reverse().find((h) => statuses.includes(h.status));
  };
  const currentEvent = history?.length ? history[history.length - 1] : undefined;
  const currentRole = ROLE_LABELS[currentEvent?.role] || '';
  const motive = (control.motive || '').trim();

  const validityText = control.validUntil
    ? formatDateOnly(control.validUntil)
    : currentIndex === STAGES.length - 1
    ? 'Sem prazo de validade'
    : 'Definida ao concluir a auditoria';

  return (
    <Box bg='white' p='24px' borderRadius='12px' mb='16px'>
      <Flex align='center' gap='12px' mb='24px' wrap='wrap'>
        <Text fontWeight={700}>Acompanhamento das etapas</Text>
        <Flex align='center' gap='8px' ml='auto'>
          <Text fontWeight={700} fontSize='sm' color='#5C6066'>
            Validade:
          </Text>
          <Text fontWeight={600} color={control.validUntil ? '#16262E' : '#9CA0A6'}>
            {validityText}
          </Text>
        </Flex>
      </Flex>

      <Flex align='flex-start'>
        {STAGES.map((stage, index) => {
          const isDone = index < currentIndex;
          const isActive = index === currentIndex;
          const reached = index <= currentIndex;
          const event = lastEventForStage(stage.statuses);
          const color = isDone ? '#21B84C' : isActive ? '#0073BB' : '#C7CBD1';
          const date = reached
            ? formatDate(event?.createdAt || (isActive ? control.updatedAt : ''))
            : '';

          return (
            <Flex
              key={stage.label}
              direction='column'
              align='center'
              flex='1'
              pos='relative'
              textAlign='center'
            >
              {index > 0 && (
                <Box
                  pos='absolute'
                  top='8px'
                  left='-50%'
                  w='100%'
                  h='2px'
                  bg={reached ? '#21B84C' : '#EAEEF0'}
                  zIndex={0}
                />
              )}

              {isDone ? (
                <Icon as={CheckIcon} boxSize='18px' color={color} zIndex={1} bg='white' />
              ) : (
                <Circle
                  size='18px'
                  border='2px solid'
                  borderColor={color}
                  bg={isActive ? color : 'white'}
                  zIndex={1}
                />
              )}

              <Text
                mt='8px'
                px='4px'
                fontSize='sm'
                fontWeight={isActive ? 700 : 600}
                color={isDone || isActive ? '#16262E' : '#9CA0A6'}
              >
                {stage.label}
              </Text>
              {date && (
                <Text fontSize='xs' color='#9CA0A6' px='4px'>
                  {date}
                </Text>
              )}
            </Flex>
          );
        })}
      </Flex>

      {/* Status atual + motivo (único lugar onde o motivo aparece, formatado). */}
      <Box mt='24px' borderRadius='12px' border='1px solid #EAEEF0' overflow='hidden'>
        <Flex align='center' justify='space-between' gap='8px' bg='#F8F8F8' p='12px 16px'>
          <Flex align='center' gap='10px'>
            <Circle size='10px' bg={STATUS_COLOR[control.status] || '#0073BB'} />
            <Text fontSize='sm' fontWeight={700} color='#16262E'>
              {STATUS_LABELS[control.status] || control.status}
            </Text>
          </Flex>
          {currentRole && (
            <Text fontSize='sm' color='#9CA0A6' whiteSpace='nowrap'>
              por {currentRole}
            </Text>
          )}
        </Flex>
        {motive && (
          <Box
            p='14px 16px'
            bg='white'
            borderLeft='3px solid'
            borderLeftColor={STATUS_COLOR[control.status] || '#0073BB'}
          >
            <Text
              fontSize='11px'
              fontWeight={700}
              color='#9CA0A6'
              letterSpacing='0.5px'
              mb='4px'
            >
              MOTIVO
            </Text>
            <Text fontSize='sm' color='#16262E' lineHeight='1.5'>
              {motive}
            </Text>
          </Box>
        )}
      </Box>

      {control.validUntil && (
        <Text fontSize='sm' color='#21B84C' fontWeight={600} mt='12px'>
          Válido até {formatDateOnly(control.validUntil)}
        </Text>
      )}
    </Box>
  );
}

// Check simples (evita dependência de lib de ícone específica no admin).
function CheckIcon(props) {
  return (
    <svg viewBox='0 0 24 24' fill='none' {...props}>
      <circle cx='12' cy='12' r='10' fill='currentColor' />
      <path
        d='M8 12.5l2.5 2.5L16 9'
        stroke='white'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}
